import { Link } from "react-router-dom";
import { featuredEpisode, comingSoonEpisodes } from "../../../mocks/episodes";
import AudioPlayer from "../../../components/feature/AudioPlayer";

const COVER_ART = "https://static.readdy.ai/image/f9f32838a9120faa908a5852ba765d83/f8910ea58b264f83bfa3194cce1f37cf.png";

export default function EpisodesPreview() {
  return (
    <section className="bg-parchment py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block font-sans text-xs font-semibold tracking-widest uppercase text-amber-600 bg-amber-100 rounded-full px-4 py-1.5 mb-5">
            {featuredEpisode.comingSoon ? "Coming Soon" : "Latest Episode"}
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-amber-900 mb-5">
            {featuredEpisode.comingSoon ? "Episode 1 Drops Soon" : "Start Here"}
          </h2>
          <p className="font-sans text-base text-stone-500 max-w-lg mx-auto leading-relaxed">
            {featuredEpisode.comingSoon
              ? `Mark your calendar — Episode 1 launches ${featuredEpisode.launchDate}. Subscribe so you don\u2019t miss it.`
              : "New to the show? Episode 1 is the perfect place to begin. More episodes are on the way."}
          </p>
        </div>

        {/* Featured episode — horizontal card */}
        <div className="bg-white rounded-3xl overflow-hidden mb-10 group hover:-translate-y-1 transition-all duration-300">
          <div className="grid grid-cols-1 md:grid-cols-5">

            {/* Cover art */}
            <div
              className="md:col-span-2 flex items-center justify-center p-10 min-h-[260px]"
              style={{ background: "linear-gradient(135deg, #3b1505 0%, #78350f 60%, #a85510 100%)" }}
            >
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-amber-400/25 blur-2xl scale-90 translate-y-3" />
                <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-amber-400/30 group-hover:ring-amber-400/60 transition-all duration-300">
                  <img
                    src={COVER_ART}
                    alt="Episode 1 Cover"
                    className="w-44 h-44 md:w-52 md:h-52 object-cover"
                  />
                </div>
                <div className="absolute -top-2 -right-2 bg-amber-500 text-amber-950 font-sans font-bold text-xs px-2.5 py-1 rounded-full shadow whitespace-nowrap">
                  Ep. 1
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-sans text-xs font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
                  {featuredEpisode.category}
                </span>
                {featuredEpisode.comingSoon ? (
                  <span className="inline-flex items-center gap-1.5 font-sans text-xs text-amber-600 font-semibold">
                    <i className="ri-calendar-event-line text-xs" />
                    Launches {featuredEpisode.launchDate}
                  </span>
                ) : (
                  <span className="font-sans text-xs text-stone-400">
                    {featuredEpisode.duration} &bull; {featuredEpisode.date}
                  </span>
                )}
              </div>

              <h3 className="font-serif text-xl md:text-2xl font-bold text-amber-900 leading-snug mb-4">
                {featuredEpisode.title}
              </h3>

              <p className="font-sans text-sm text-stone-500 leading-relaxed mb-7 line-clamp-3">
                {featuredEpisode.description}
              </p>

              {featuredEpisode.comingSoon ? (
                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 font-sans font-semibold text-sm px-5 py-2.5 rounded-full whitespace-nowrap">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-lock-line text-sm" />
                    </span>
                    Coming Soon
                  </div>
                  <Link
                    to="/episodes"
                    className="font-sans text-xs text-amber-600 hover:text-amber-800 underline underline-offset-2 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    See details
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {/* Compact inline player */}
                  <AudioPlayer
                    src={featuredEpisode.audioUrl}
                    duration={featuredEpisode.duration}
                    variant="compact"
                  />
                  {/* Platform icons */}
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-xs text-stone-400 mr-1">Also on</span>
                    <a href={featuredEpisode.spotifyUrl} rel="nofollow" aria-label="Spotify" className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 transition-colors cursor-pointer">
                      <i className="ri-spotify-fill text-amber-700 text-sm" />
                    </a>
                    <a href={featuredEpisode.appleUrl} rel="nofollow" aria-label="Apple Podcasts" className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 transition-colors cursor-pointer">
                      <i className="ri-apple-fill text-amber-700 text-sm" />
                    </a>
                    <Link
                      to="/episodes"
                      className="ml-auto font-sans text-xs font-semibold text-amber-600 hover:text-amber-800 underline underline-offset-2 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Episode details &rarr;
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coming soon mini row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {comingSoonEpisodes.slice(0, 3).map((ep) => (
            <div key={ep.id} className="bg-white/70 rounded-2xl p-5 flex items-center gap-4 border border-amber-100 hover:-translate-y-1 hover:bg-white hover:border-amber-200 transition-all duration-200 cursor-default">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
                <i className={`${ep.icon} text-amber-600 text-lg`} />
              </div>
              <div className="min-w-0">
                <p className="font-sans text-xs text-amber-500 font-semibold tracking-wide mb-0.5">Ep. {ep.number} &bull; Coming Soon</p>
                <p className="font-serif font-bold text-amber-800 text-sm truncate">{ep.teaserTitle}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/episodes"
            className="inline-flex items-center gap-2 border-2 border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-amber-950 active:scale-95 font-sans font-semibold text-sm px-8 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            See All Episodes
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </Link>
        </div>

      </div>
    </section>
  );
}
